import React, { useState, useEffect } from 'react';
import { ChartBarIcon, ChatBubbleLeftRightIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { analyticsService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';

const AnalyticsSummary = () => {
  const { t } = useLanguage();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get date range for last 30 days
  const getDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      const dateRange = getDateRange();
      const response = await analyticsService.getSummary(dateRange);

      if (response.success && response.data?.analytics) {
        setAnalyticsData(response.data.analytics);
      } else {
        setError('No analytics data available');
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const formatPercentage = (value) => {
    if (typeof value === 'number') {
      return `${(value * 100).toFixed(1)}%`;
    }
    return value;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#075e54]"></div>
          <span className="ml-3 text-gray-600">Loading analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">{error}</p>
            <button
              onClick={fetchAnalytics}
              className="mt-2 text-[#075e54] hover:text-[#064a42] text-sm font-medium"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return null;
  }

  // Hide analytics overview if there are zero replies
  if (!analyticsData.conversations_with_reply || analyticsData.conversations_with_reply === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <ChartBarIcon className="h-6 w-6 text-[#075e54] mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Analytics Overview</h2>
        </div>
        <span className="text-sm text-gray-500">Last 30 days</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Conversations */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 text-blue-600">
              <ChatBubbleLeftRightIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-blue-600">Total Conversations</p>
              <p className="text-2xl font-bold text-blue-900">
                {analyticsData.total_conversations || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Conversations with Reply */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 text-green-600">
              <CheckCircleIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-green-600">With Reply</p>
              <p className="text-2xl font-bold text-green-900">
                {analyticsData.conversations_with_reply || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Reply Rate */}
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 text-purple-600">
              <ClockIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-purple-600">Reply Rate</p>
              <p className="text-2xl font-bold text-purple-900">
                {formatPercentage(analyticsData.conversations_with_reply_percentage)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Insights */}
      {/* <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-600">Average conversations per day</p>
            <p className="text-lg font-semibold text-gray-900">
              {analyticsData.total_conversations ?
                Math.round(analyticsData.total_conversations / 30) : 0}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Engagement level</p>
            <p className="text-lg font-semibold text-gray-900">
              {analyticsData.conversations_with_reply_percentage > 0.7 ? 'High' :
               analyticsData.conversations_with_reply_percentage > 0.4 ? 'Medium' : 'Low'}
            </p>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default AnalyticsSummary;
